/**
 * Shared identifiers and value types used by both the signaling protocol
 * (peer <-> tracker) and the segment-exchange protocol (peer <-> peer).
 *
 * This file is the single source of truth for the wire contract. Native
 * (iOS/Android) and TV implementations MUST mirror these shapes so a peer on
 * any platform can join the same swarm and speak to any other peer.
 */

/** Opaque per-session peer identity. Generated client-side (UUID). */
export type PeerId = string;

/** Identifies a live channel / swarm. All peers with the same streamId share. */
export type StreamId = string;

/**
 * Identifies one HLS segment within a stream. We key on the resolved URL so it
 * is stable across peers regardless of how the playlist is served, plus the
 * media sequence number which lets the scheduler reason about playback order
 * and deadlines for LIVE without re-parsing the manifest.
 */
export interface SegmentId {
  /** Absolute, normalized segment URL. Stable swarm-wide dedupe key. */
  readonly url: string;
  /** HLS media sequence number (monotonic per stream). -1 if unknown. */
  readonly sn: number;
}

/**
 * Network-proximity hint the tracker derives from the peer's IP. Used to rank
 * candidates "nearest first" before any RTT is measured (Tier 1 selection).
 */
export interface GeoHint {
  /** Autonomous System Number (ISP). Same ASN => usually lowest RTT + no peering cost. */
  readonly asn: number | null;
  /** ISO country code, e.g. "VN". */
  readonly country: string | null;
  /** Coarse region/city bucket, e.g. "VN-HN". Implementation-defined granularity. */
  readonly region: string | null;
}

export const SEGMENT_ID_SEPARATOR = " ";

/** Canonical string key for a segment (for Set/Map usage). */
export function segmentKey(id: SegmentId): string {
  return `${id.sn}${SEGMENT_ID_SEPARATOR}${id.url}`;
}
